import {
  Banner,
  Button,
  Buttons,
  Container,
  ControlGroup,
  Form,
  Group,
  Header,
  Input,
  IControlChangeEventArgs,
  Layout,
  Level,
  LevelGroup,
  LevelItem,
  MainContent,
  Section,
  Select,
} from '@inmotionnow/momentum-components-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { useOutcome } from '@/app/outcome-context'
import {
  type CreateAccountWFRequest,
  useGetProvisioningOptionsQuery,
} from '@/features/accounts/accountApi'
import { useCreateAccount } from '@/features/accounts/useCreateAccount'
import { generateId } from '@/utils/id-generator'
import {
  isValidEmail,
  isValidSubdomain,
  isValidSubdomainRealtime,
} from '@/utils/validation'

export interface IAccountCreateComponentProps {
  /**
   * When true, render the form without the page chrome (Layout/Header/Banner)
   * so an embedding host (Workflow/DAM) supplies its own header — avoids the
   * duplicate header row when embedded (KPS-450). Mirrors the user embed, which
   * is chrome-less for the same reason.
   */
  embedded?: boolean
}

const accountCreateFormId = 'account-create-form'

interface AccountFormData {
  DisplayName: string
  Subdomain: string
  ContactEmail: string
  FirstName: string
  LastName: string
  Locale: string
  /** Number of team members; kept as string in form, converted on save */
  NumberOfTeamMembers: string
  /** WF provisioning options; -1 = not selected (0 is a valid option ID) */
  TemplateId: number
  TypeId: number
  PricingPlanId: number
}

const sectionHeadingStyle: React.CSSProperties = {
  marginBottom: '1rem',
  fontSize: '1.125rem',
  fontWeight: 600,
  color: '#5a5a5a',
}

const AccountCreateView = ({
  embedded = false,
}: IAccountCreateComponentProps) => {
  const { t } = useTranslation()
  const { onCancel } = useOutcome()
  const {
    data: provisioningOptions,
    isLoading: isLoadingOptions,
    isError: isProvisioningError,
    refetch: refetchProvisioning,
  } = useGetProvisioningOptionsQuery()

  const hasProvisioningOptions =
    !!provisioningOptions &&
    (provisioningOptions.Template?.length ?? 0) > 0 &&
    (provisioningOptions.Type?.length ?? 0) > 0 &&
    (provisioningOptions.PricingPlan?.length ?? 0) > 0
  const provisioningOptionsUnavailable =
    !isLoadingOptions &&
    !isProvisioningError &&
    provisioningOptions !== undefined &&
    !hasProvisioningOptions

  const { submitCreateAccount, isLoading, isError, error } = useCreateAccount()
  const [validationErrors, setValidationErrors] = React.useState<
    Partial<Record<keyof AccountFormData, string>>
  >({})

  const [formData, setFormData] = React.useState<AccountFormData>({
    DisplayName: '',
    Subdomain: '',
    ContactEmail: '',
    FirstName: '',
    LastName: '',
    Locale: 'en-US',
    NumberOfTeamMembers: '',
    TemplateId: -1,
    TypeId: -1,
    PricingPlanId: -1,
  })

  const validateForm = React.useCallback(() => {
    const errors: Partial<Record<keyof AccountFormData, string>> = {}

    if (!formData.DisplayName.trim()) {
      errors.DisplayName = t('ACCOUNT_NAME_REQUIRED')
    }

    if (!formData.Subdomain.trim()) {
      errors.Subdomain = t('ACCOUNT_SUBDOMAIN_REQUIRED')
    } else if (!isValidSubdomain(formData.Subdomain)) {
      errors.Subdomain = t('SUBDOMAIN_LOWERCASE_ALPHANUMERIC')
    }

    if (!formData.ContactEmail.trim()) {
      errors.ContactEmail = t('CONTACT_EMAIL_REQUIRED')
    } else if (!isValidEmail(formData.ContactEmail)) {
      errors.ContactEmail = t('CONTACT_EMAIL_INVALID')
    }

    if (!formData.FirstName.trim()) {
      errors.FirstName = t('ADMIN_FIRST_NAME_REQUIRED')
    } else if (formData.FirstName.trim().length > 30) {
      errors.FirstName = t('FIRST_NAME_MAX_LENGTH', { max: 30 })
    }
    if (!formData.LastName.trim()) {
      errors.LastName = t('ADMIN_LAST_NAME_REQUIRED')
    } else if (formData.LastName.trim().length > 30) {
      errors.LastName = t('LAST_NAME_MAX_LENGTH', { max: 30 })
    }

    if (!formData.Locale.trim()) {
      errors.Locale = t('ADMIN_LOCALE_REQUIRED')
    }

    // WF provisioning only: Template, Type, and Pricing Plan are required and always sent in the payload.
    if (formData.TemplateId === -1) {
      errors.TemplateId = t('PROVISIONING_TEMPLATE_REQUIRED')
    }
    if (formData.TypeId === -1) {
      errors.TypeId = t('PROVISIONING_TYPE_REQUIRED')
    }
    if (formData.PricingPlanId === -1) {
      errors.PricingPlanId = t('PROVISIONING_PRICING_PLAN_REQUIRED')
    }

    if (!formData.NumberOfTeamMembers.trim()) {
      errors.NumberOfTeamMembers = t('NUMBER_OF_TEAM_MEMBERS_REQUIRED')
    } else {
      const num = parseInt(formData.NumberOfTeamMembers, 10)
      if (Number.isNaN(num) || num < 1) {
        errors.NumberOfTeamMembers = t('NUMBER_OF_TEAM_MEMBERS_INVALID')
      }
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }, [formData, t])

  const handleFieldChange = React.useCallback(
    (field: keyof AccountFormData) => (e: IControlChangeEventArgs) => {
      let value = e.currentValue

      if (field === 'Subdomain') {
        const isValid = isValidSubdomainRealtime(value)
        if (!isValid && value !== '') {
          return
        }
      }

      if (field === 'NumberOfTeamMembers') {
        value = String(value ?? '').replace(/\D/g, '')
      }

      setFormData(prev => ({ ...prev, [field]: value }))

      if (validationErrors[field]) {
        setValidationErrors(prev => {
          const { [field]: _, ...rest } = prev
          return rest
        })
      }
    },
    [validationErrors],
  )

  const handleProvisioningSelect = React.useCallback(
    (field: 'TemplateId' | 'TypeId' | 'PricingPlanId') =>
      (e: IControlChangeEventArgs) => {
        const raw = e.currentValue
        const parsed = raw === '' || raw == null ? -1 : Number(raw)
        const value = Number.isFinite(parsed) ? parsed : -1
        setFormData(prev => ({ ...prev, [field]: value }))
        setValidationErrors(prev => {
          const { [field]: _, ...rest } = prev
          return rest
        })
      },
    [],
  )

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (
      isProvisioningError ||
      isLoadingOptions ||
      provisioningOptionsUnavailable
    ) {
      return
    }
    if (!validateForm()) {
      return
    }

    const numTeamMembers = parseInt(formData.NumberOfTeamMembers, 10)
    const requestBody: CreateAccountWFRequest = {
      account: {
        keycloak_realm_id: formData.Subdomain,
        display_name: formData.DisplayName,
        contact_email: formData.ContactEmail,
        is_active: true,
        workflow_team_member_count:
          Number.isInteger(numTeamMembers) && numTeamMembers >= 1
            ? numTeamMembers
            : 1,
      },
      admin_user: {
        email: formData.ContactEmail,
        first_name: formData.FirstName.trim(),
        last_name: formData.LastName.trim(),
        locale: (formData.Locale || '').trim() || 'us',
        timezone: 'America/New_York',
      },
      provisioning: {
        template_id: formData.TemplateId,
        type_id: formData.TypeId,
        pricing_plan_id: formData.PricingPlanId,
      },
    }

    submitCreateAccount(requestBody)
  }

  const isProcessing = isLoading

  const handleCancel = () => {
    onCancel()
  }

  const getErrorMessage = () => {
    if (!error) return t('UNKNOWN_ERROR')

    // Map backend field names to user-friendly names
    const fieldNameMap: Record<string, string> = {
      PlatformAccount: t('Account'),
      display_name: t('ACCOUNT_NAME'),
      keycloak_realm_id: t('ACCOUNT_SUBDOMAIN'),
      contact_email: t('CONTACT_EMAIL'),
    }

    if (
      typeof error === 'object' &&
      'data' in error &&
      error.data &&
      typeof error.data === 'object'
    ) {
      const errorData = error.data

      // Check for nested fields structure
      if (
        'extra' in errorData &&
        errorData.extra &&
        typeof errorData.extra === 'object' &&
        'fields' in errorData.extra &&
        errorData.extra.fields &&
        typeof errorData.extra.fields === 'object'
      ) {
        const fields = errorData.extra.fields as Record<string, string[]>
        let fieldErrors = Object.entries(fields)
          .flatMap(([_, messages]) => messages)
          .join(', ')

        Object.entries(fieldNameMap).forEach(([backendName, friendlyName]) => {
          const regex = new RegExp(backendName, 'gi')
          fieldErrors = fieldErrors.replace(regex, friendlyName)
        })

        const message =
          'message' in errorData && typeof errorData.message === 'string'
            ? errorData.message
            : undefined

        return fieldErrors || message || t('UNKNOWN_ERROR')
      }

      // Check for simple message
      if ('message' in errorData && typeof errorData.message === 'string') {
        return errorData.message
      }
    }

    if (
      typeof error === 'object' &&
      'message' in error &&
      typeof (error as Record<string, unknown>).message === 'string'
    ) {
      return (error as Record<string, unknown>).message as string
    }

    return t('UNKNOWN_ERROR')
  }

  const body = (
    <MainContent>
      <Section>
        <Container size='sm'>
          {(isProvisioningError || provisioningOptionsUnavailable) && (
            <div
              style={{
                padding: '1rem',
                marginBottom: '1rem',
                backgroundColor: '#fef3cd',
                border: '1px solid #f0c674',
                borderRadius: '4px',
                color: '#856404',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '0.5rem',
              }}
              data-testid='provisioning-options-error'
            >
              <span>
                {isProvisioningError
                  ? t('PROVISIONING_OPTIONS_LOAD_ERROR')
                  : t('PROVISIONING_OPTIONS_EMPTY')}
              </span>
              <Button
                label={t('RETRY')}
                onClick={() => refetchProvisioning()}
                theme='flat'
              />
            </div>
          )}
          {isError && (
            <div
              style={{
                padding: '1rem',
                marginBottom: '1rem',
                backgroundColor: '#fee',
                border: '1px solid #fcc',
                borderRadius: '4px',
                color: '#c00',
              }}
              data-testid='error-message'
            >
              {t('ERROR_CREATING_ACCOUNT')}: {getErrorMessage()}
            </div>
          )}
          <Form id={accountCreateFormId} onSubmit={handleSubmit}>
            <Group tag='div'>
              <h2 style={sectionHeadingStyle}>{t('ACCOUNT_INFORMATION')}</h2>
              <ControlGroup
                control={
                  <Input
                    dataTestingId={generateId('input', 'account-name')}
                    dataTrackingId={generateId('input', 'account-name')}
                    disabled={isProcessing}
                    handleChange={handleFieldChange('DisplayName')}
                    hasError={!!validationErrors.DisplayName}
                    id='account-name'
                    required
                    maxLength={512}
                    value={formData.DisplayName}
                  />
                }
                helpText={validationErrors.DisplayName}
                htmlFor='account-name'
                label={t('FORM_LABEL_NAME')}
              />
              <ControlGroup
                control={
                  <Input
                    dataTestingId={generateId('input', 'account-subdomain')}
                    dataTrackingId={generateId('input', 'account-subdomain')}
                    disabled={isProcessing}
                    handleChange={handleFieldChange('Subdomain')}
                    hasError={!!validationErrors.Subdomain}
                    id='account-subdomain'
                    maxLength={256}
                    placeholder={t('ACCOUNT_SUBDOMAIN_PLACEHOLDER')}
                    required
                    value={formData.Subdomain}
                  />
                }
                helpText={validationErrors.Subdomain}
                htmlFor='account-subdomain'
                label={t('FORM_LABEL_SUBDOMAIN')}
              />
              <ControlGroup
                control={
                  <Input
                    dataTestingId={generateId('input', 'account-email')}
                    dataTrackingId={generateId('input', 'account-email')}
                    disabled={isProcessing}
                    handleChange={handleFieldChange('ContactEmail')}
                    hasError={!!validationErrors.ContactEmail}
                    id='account-email'
                    required
                    type='email'
                    value={formData.ContactEmail}
                  />
                }
                helpText={validationErrors.ContactEmail}
                htmlFor='account-email'
                label={t('CONTACT_EMAIL')}
              />
            </Group>

            <div style={{ marginTop: '2.75rem' }}>
              <Group tag='div'>
                <h2 style={sectionHeadingStyle}>{t('ADMIN_USER_DETAILS')}</h2>
                <ControlGroup
                  control={
                    <Input
                      dataTestingId={generateId('input', 'admin-first-name')}
                      dataTrackingId={generateId('input', 'admin-first-name')}
                      disabled={isProcessing}
                      handleChange={handleFieldChange('FirstName')}
                      hasError={!!validationErrors.FirstName}
                      id='admin-first-name'
                      placeholder={t('ADMIN_FIRST_NAME_PLACEHOLDER')}
                      required
                      maxLength={30}
                      value={formData.FirstName}
                    />
                  }
                  helpText={validationErrors.FirstName}
                  htmlFor='admin-first-name'
                  label={t('ADMIN_FIRST_NAME')}
                />
                <ControlGroup
                  control={
                    <Input
                      dataTestingId={generateId('input', 'admin-last-name')}
                      dataTrackingId={generateId('input', 'admin-last-name')}
                      disabled={isProcessing}
                      handleChange={handleFieldChange('LastName')}
                      hasError={!!validationErrors.LastName}
                      id='admin-last-name'
                      placeholder={t('ADMIN_LAST_NAME_PLACEHOLDER')}
                      required
                      maxLength={30}
                      value={formData.LastName}
                    />
                  }
                  helpText={validationErrors.LastName}
                  htmlFor='admin-last-name'
                  label={t('ADMIN_LAST_NAME')}
                />
                <ControlGroup
                  control={
                    <Input
                      dataTestingId={generateId('input', 'account-locale')}
                      dataTrackingId={generateId('input', 'account-locale')}
                      disabled={isProcessing}
                      handleChange={handleFieldChange('Locale')}
                      hasError={!!validationErrors.Locale}
                      id='account-locale'
                      required
                      value={formData.Locale}
                    />
                  }
                  helpText={validationErrors.Locale}
                  htmlFor='account-locale'
                  label={t('ADMIN_LOCALE')}
                />
              </Group>
            </div>

            <div style={{ marginTop: '2.75rem' }}>
              <Group tag='div'>
                <h2 style={sectionHeadingStyle}>{t('PROVISIONING_OPTIONS')}</h2>
                <ControlGroup
                  control={
                    <Select
                      id='provisioning-template'
                      selectedValue={formData.TemplateId}
                      selectOptions={[
                        { value: -1, displayText: t('PROVISIONING_SELECT') },
                        ...(provisioningOptions?.Template ?? []).map(item => ({
                          value: item.Id,
                          displayText: item.Value,
                        })),
                      ]}
                      disabled={isProcessing || isLoadingOptions}
                      handleChange={handleProvisioningSelect('TemplateId')}
                      required
                      hasError={!!validationErrors.TemplateId}
                    />
                  }
                  helpText={validationErrors.TemplateId}
                  htmlFor='provisioning-template'
                  label={t('PROVISIONING_TEMPLATE')}
                />
                <ControlGroup
                  control={
                    <Select
                      id='provisioning-type'
                      selectedValue={formData.TypeId}
                      selectOptions={[
                        { value: -1, displayText: t('PROVISIONING_SELECT') },
                        ...(provisioningOptions?.Type ?? []).map(item => ({
                          value: item.Id,
                          displayText: item.Value,
                        })),
                      ]}
                      disabled={isProcessing || isLoadingOptions}
                      handleChange={handleProvisioningSelect('TypeId')}
                      required
                      hasError={!!validationErrors.TypeId}
                    />
                  }
                  helpText={validationErrors.TypeId}
                  htmlFor='provisioning-type'
                  label={t('PROVISIONING_TYPE')}
                />
                <ControlGroup
                  control={
                    <Select
                      id='provisioning-pricing-plan'
                      selectedValue={formData.PricingPlanId}
                      selectOptions={[
                        { value: -1, displayText: t('PROVISIONING_SELECT') },
                        ...(provisioningOptions?.PricingPlan ?? []).map(
                          item => ({
                            value: item.Id,
                            displayText: item.Value,
                          }),
                        ),
                      ]}
                      disabled={isProcessing || isLoadingOptions}
                      handleChange={handleProvisioningSelect('PricingPlanId')}
                      required
                      hasError={!!validationErrors.PricingPlanId}
                    />
                  }
                  helpText={validationErrors.PricingPlanId}
                  htmlFor='provisioning-pricing-plan'
                  label={t('PROVISIONING_PRICING_PLAN')}
                />
                <ControlGroup
                  control={
                    <Input
                      dataTestingId={generateId(
                        'input',
                        'account-team-members',
                      )}
                      dataTrackingId={generateId(
                        'input',
                        'account-team-members',
                      )}
                      disabled={isProcessing}
                      handleChange={handleFieldChange('NumberOfTeamMembers')}
                      hasError={!!validationErrors.NumberOfTeamMembers}
                      id='account-team-members'
                      required
                      type='number'
                      value={formData.NumberOfTeamMembers}
                    />
                  }
                  helpText={validationErrors.NumberOfTeamMembers}
                  htmlFor='account-team-members'
                  label={t('NUMBER_OF_TEAM_MEMBERS')}
                />
              </Group>
            </div>

            <div style={{ marginTop: '2.5rem' }}>
              <Level>
                <LevelGroup position='center'>
                  <LevelItem>
                    <Buttons>
                      <Button
                        label={t('CANCEL')}
                        onClick={handleCancel}
                        theme='flat'
                      />
                      <Form.Submit
                        formId={accountCreateFormId}
                        label={
                          isProcessing
                            ? t('ACCOUNT_CREATION_IN_PROGRESS')
                            : t('CREATE_ACCOUNT')
                        }
                        loading={isProcessing}
                        disabled={
                          isLoadingOptions ||
                          isProvisioningError ||
                          provisioningOptionsUnavailable ||
                          isProcessing
                        }
                      />
                    </Buttons>
                  </LevelItem>
                </LevelGroup>
              </Level>
            </div>
          </Form>
        </Container>
      </Section>
    </MainContent>
  )

  if (embedded) {
    return body
  }

  return (
    <Layout>
      <Header>
        <Banner title={t('CREATE_ACCOUNT')} />
      </Header>
      {body}
    </Layout>
  )
}

export { AccountCreateView }
